<template>
  <div class="sponsor-report-page">
    <MenuBar />

    <div class="content">
      <div class="page-header">
        <div>
          <h1>Sponsor Report Section</h1>
          <p>Generate public sponsor report links and configure views delay settings.</p>
        </div>
      </div>

      <section class="card settings-card">
        <h2>Global Report Settings</h2>
        <div class="settings-grid">
          <div class="field">
            <label for="views-delay">Views Delay (Days)</label>
            <input id="views-delay" v-model.number="settings.viewsDelayDays" type="number" min="0" />
          </div>
          <div class="field checkbox-field">
            <label>
              <input v-model="settings.youtubeEnabled" type="checkbox" />
              Enable YouTube Views
            </label>
          </div>
          <div class="field checkbox-field">
            <label>
              <input v-model="settings.facebookEnabled" type="checkbox" />
              Show Facebook Section
            </label>
          </div>
          <div class="actions">
            <button class="btn" :disabled="savingSettings" @click="saveSettings">{{ savingSettings ? 'Saving...' : 'Save Settings' }}</button>
          </div>
        </div>
      </section>

      <section class="card selector-card">
        <h2>Sponsor Report Links</h2>
        <div class="selector-grid">
          <div class="field field-wide">
            <label for="sponsor-search">Search Sponsor</label>
            <input id="sponsor-search" v-model.trim="search" type="text" placeholder="Search sponsor name" />
          </div>
          <div class="field field-wide">
            <label for="sponsor-select">Select Sponsor</label>
            <select id="sponsor-select" v-model="selectedSponsorId">
              <option value="">Choose sponsor</option>
              <option v-for="item in filteredSponsors" :key="item.id" :value="item.id">{{ item.sponsorName }}</option>
            </select>
          </div>
          <div class="field field-wide">
            <label for="campaign-select">Campaign (optional)</label>
            <select id="campaign-select" v-model="selectedLinkCampaignId" :disabled="!selectedSponsorId || loadingCampaigns">
              <option value="">All Campaigns</option>
              <option v-for="c in sponsorCampaigns" :key="c.id" :value="c.id">{{ c.campaignName }}</option>
            </select>
          </div>
          <div class="actions">
            <button class="btn" :disabled="!selectedSponsorId || creatingLink" @click="createShareLink">{{ creatingLink ? 'Generating...' : 'Generate Public Link' }}</button>
          </div>
        </div>

        <p v-if="lastPublicUrl" class="public-url">
          Latest Public URL:
          <a :href="lastPublicUrl" target="_blank" rel="noopener noreferrer">{{ lastPublicUrl }}</a>
        </p>

        <div v-if="shareLinks.length" class="table-wrap">
          <table class="report-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Public Link</th>
                <th>Created By</th>
                <th>Created At</th>
                <th>Access Count</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in shareLinks" :key="item.id">
                <td>{{ item.campaignName || 'All' }}</td>
                <td class="link-cell">
                  <a :href="buildPublicUrl(item.token)" target="_blank" rel="noopener noreferrer" class="link-text">{{ buildPublicUrl(item.token) }}</a>
                  <button class="btn copy-btn" :class="{ copied: copiedId === item.id }" :title="copiedId === item.id ? 'Copied!' : 'Copy link'" @click="copyLink(item)">
                    {{ copiedId === item.id ? 'Copied!' : 'Copy' }}
                  </button>
                </td>
                <td>{{ item.createdBy || '-' }}</td>
                <td>{{ humanDateTime(item.createdAt) }}</td>
                <td>{{ Number(item.accessCount || 0) }}</td>
                <td>
                  <span class="status-pill" :class="item.isRevoked ? 'revoked' : 'active'">{{ item.isRevoked ? 'Revoked' : 'Active' }}</span>
                </td>
                <td>
                  <button class="btn danger" :disabled="item.isRevoked || revokingId === item.id" @click="revokeLink(item)">
                    {{ revokingId === item.id ? 'Revoking...' : 'Revoke' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty">No report links for selected sponsor.</p>
      </section>

      <p v-if="message" class="message">{{ message }}</p>
    </div>
  </div>
</template>

<script>
import MenuBar from './MenuBar.vue';
import sponsorService from '../services/sponsorService';

export default {
  name: 'SponsorReportSection',
  components: { MenuBar },
  data() {
    return {
      sponsors: [],
      selectedSponsorId: '',
      selectedLinkCampaignId: '',
      sponsorCampaigns: [],
      loadingCampaigns: false,
      shareLinks: [],
      lastPublicUrl: '',
      search: '',
      message: '',
      creatingLink: false,
      revokingId: '',
      copiedId: '',
      savingSettings: false,
      settings: {
        viewsDelayDays: 3,
        youtubeEnabled: true,
        facebookEnabled: true,
      },
    };
  },
  computed: {
    filteredSponsors() {
      const key = String(this.search || '').toLowerCase();
      if (!key) return this.sponsors;
      return this.sponsors.filter(item => String(item.sponsorName || '').toLowerCase().includes(key));
    },
  },
  watch: {
    selectedSponsorId() {
      this.lastPublicUrl = '';
      this.selectedLinkCampaignId = '';
      this.sponsorCampaigns = [];
      this.loadShareLinks();
      this.loadSponsorCampaigns();
    },
  },
  mounted() {
    this.loadSponsors();
    this.loadSettings();
  },
  methods: {
    async loadSponsors() {
      try {
        const response = await sponsorService.getSponsors();
        this.sponsors = response.items || [];
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load sponsors';
      }
    },
    async loadSettings() {
      try {
        const response = await sponsorService.getReportSettings();
        this.settings = {
          viewsDelayDays: Number(response.item && response.item.viewsDelayDays || 3),
          youtubeEnabled: !!(response.item && response.item.youtubeEnabled),
          facebookEnabled: !!(response.item && response.item.facebookEnabled),
        };
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load report settings';
      }
    },
    async saveSettings() {
      this.savingSettings = true;
      this.message = '';
      try {
        const payload = {
          viewsDelayDays: Math.max(Number(this.settings.viewsDelayDays || 0), 0),
          youtubeEnabled: !!this.settings.youtubeEnabled,
          facebookEnabled: !!this.settings.facebookEnabled,
        };
        const response = await sponsorService.updateReportSettings(payload);
        this.settings = {
          viewsDelayDays: Number(response.item && response.item.viewsDelayDays || 3),
          youtubeEnabled: !!(response.item && response.item.youtubeEnabled),
          facebookEnabled: !!(response.item && response.item.facebookEnabled),
        };
        this.message = 'Report settings updated successfully';
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to update report settings';
      } finally {
        this.savingSettings = false;
      }
    },
    async loadSponsorCampaigns() {
      if (!this.selectedSponsorId) return;
      this.loadingCampaigns = true;
      try {
        const response = await sponsorService.getCampaigns(this.selectedSponsorId);
        this.sponsorCampaigns = response.items || [];
      } catch (error) {
        this.sponsorCampaigns = [];
      } finally {
        this.loadingCampaigns = false;
      }
    },
    async loadShareLinks() {
      this.shareLinks = [];
      if (!this.selectedSponsorId) return;
      try {
        const response = await sponsorService.listSponsorReportShareLinks(this.selectedSponsorId);
        this.shareLinks = response.items || [];
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load share links';
      }
    },
    async createShareLink() {
      if (!this.selectedSponsorId) return;
      this.creatingLink = true;
      this.message = '';
      try {
        const response = await sponsorService.createSponsorReportShareLink(this.selectedSponsorId, this.selectedLinkCampaignId);
        const token = response && response.item && response.item.token ? String(response.item.token) : '';
        this.lastPublicUrl = token ? `${window.location.origin}/public/sponsor-report/${token}` : '';
        this.message = 'Public report link generated successfully';
        await this.loadShareLinks();
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to generate report link';
      } finally {
        this.creatingLink = false;
      }
    },
    async revokeLink(item) {
      this.revokingId = item.id;
      this.message = '';
      try {
        await sponsorService.revokeSponsorReportShareLink(this.selectedSponsorId, item.id);
        this.message = 'Report link revoked successfully';
        await this.loadShareLinks();
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to revoke report link';
      } finally {
        this.revokingId = '';
      }
    },
    humanDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
    buildPublicUrl(token) {
      return `${window.location.origin}/public/sponsor-report/${token}`;
    },
    async copyLink(item) {
      const url = this.buildPublicUrl(item.token);
      try {
        await navigator.clipboard.writeText(url);
      } catch (e) {
        const el = document.createElement('textarea');
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      this.copiedId = item.id;
      setTimeout(() => { if (this.copiedId === item.id) this.copiedId = ''; }, 2000);
    },
  },
};
</script>

<style scoped>
.sponsor-report-page { display: flex; min-height: 100vh; background: #f4f7fb; }
.content { flex: 1; margin-left: 250px; padding: 24px; }
.page-header h1 { margin: 0; color: #1f2d3a; }
.page-header p { margin: 6px 0 14px; color: #607382; }
.card { background: #fff; border: 1px solid #d8e1e8; border-radius: 12px; padding: 14px; margin-bottom: 12px; }
h2 { margin: 0 0 12px; color: #1f2d3a; }
.settings-grid, .selector-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; align-items: end; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field-wide { grid-column: span 2; }
label { font-size: 12px; color: #5f7389; font-weight: 700; }
input, select { border: 1px solid #cdd9e8; border-radius: 8px; padding: 8px 10px; font-size: 12px; }
.checkbox-field { justify-content: center; }
.actions { display: flex; align-items: center; }
.btn { border: 1px solid #0e8e7f; background: #12a594; color: #fff; border-radius: 8px; padding: 8px 12px; font-weight: 700; cursor: pointer; }
.btn:disabled { opacity: 0.65; cursor: not-allowed; }
.btn.danger { border-color: #b53d3d; background: #d84a4a; }
.public-url { margin: 10px 0; font-size: 13px; color: #274657; }
.public-url a { color: #0f6aa8; font-weight: 700; }
.table-wrap { overflow-x: auto; border: 1px solid #d8e8e4; border-radius: 10px; }
.report-table { width: 100%; border-collapse: collapse; min-width: 760px; }
.report-table th, .report-table td { padding: 9px; border-bottom: 1px solid #e6f0ee; text-align: left; font-size: 12px; }
.report-table th { background: #f4faf8; color: #24475b; }
.link-cell { display: flex; align-items: center; gap: 6px; max-width: 420px; }
.link-text { font-family: monospace; font-size: 11px; color: #0f6aa8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 300px; }
.copy-btn { padding: 4px 8px; font-size: 11px; font-weight: 700; border-radius: 6px; border: 1px solid #0e8e7f; background: #12a594; color: #fff; cursor: pointer; white-space: nowrap; flex-shrink: 0; }
.copy-btn.copied { background: #0f7a50; border-color: #0f7a50; }
.status-pill { padding: 4px 8px; border-radius: 999px; font-size: 11px; font-weight: 700; }
.status-pill.active { background: #eafaf4; color: #0f7a50; }
.status-pill.revoked { background: #fdecec; color: #9f2f2f; }
.empty { margin: 8px 0 0; color: #667b8c; }
.message { margin-top: 8px; color: #1f4f75; font-weight: 700; }
@media (max-width: 768px) {
  .content { margin-left: 200px; padding: 16px; }
  .settings-grid, .selector-grid { grid-template-columns: 1fr; }
  .field-wide { grid-column: span 1; }
}
</style>
